import { cn } from '@rocco/utils/cn';
import type { Participant } from '../types';

/**
 * The type of a mention token.
 * - `U` for user/member.
 * - `P` for professional.
 */
type MentionTokenType = 'U' | 'P';

interface MentionLookupEntry {
    id: number;
    tokenType: MentionTokenType;
    displayName: string;
}

export type MentionLookup = Record<string, MentionLookupEntry>;

interface ResolvedMentionToken extends MentionLookupEntry {
    rawToken: string;
    tokenKey: string;
}

export const MENTION_TOKEN_REGEX = /<@([UP])(\d+)>/g;
const MENTION_TOKEN_CAPTURE_REGEX = /^<@([UP])(\d+)>$/;

/**
 * Build a mention lookup from chat participants.
 */
export function buildMentionLookup(participants: Participant[]): MentionLookup {
    const lookup: MentionLookup = {};
    for (const participant of participants) {
        const identity = participant.Identity;
        if (identity.__typename === 'Professional') {
            lookup[getMentionTokenKey('P', identity.ID)] = {
                id: identity.ID,
                tokenType: 'P',
                displayName: identity.Title,
            };
        } else {
            lookup[getMentionTokenKey('U', identity.ID)] = {
                id: identity.ID,
                tokenType: 'U',
                displayName: identity.Name,
            };
        }
    }
    return lookup;
}

/**
 * Build the shared lookup key used by mention tokens and `mentionLookup`.
 */
function getMentionTokenKey(tokenType: MentionTokenType, id: number) {
    return `${tokenType}${id}`;
}

/**
 * Get the mention token string for a given token type and ID.
 * @param tokenType The type of the mention token.
 * @param id The ID of the mention token.
 * @returns The mention token string.
 */
export function getMentionToken(tokenType: MentionTokenType, id: number) {
    return `<@${tokenType}${id}>`;
}

/**
 * Split text into plain text and mention token segments.
 */
export function splitTextWithMentionTokens(text: string) {
    const segments: Array<
        | { type: 'text'; value: string }
        | {
              type: 'mention';
              rawToken: string;
              tokenType: MentionTokenType;
              id: number;
          }
    > = [];
    let lastIndex = 0;
    let tokenMatch: RegExpExecArray | null;
    MENTION_TOKEN_REGEX.lastIndex = 0;
    while ((tokenMatch = MENTION_TOKEN_REGEX.exec(text)) !== null) {
        const rawToken = tokenMatch[0];
        const tokenType = tokenMatch[1] as MentionTokenType;
        const tokenId = Number(tokenMatch[2]);
        const tokenIndex = tokenMatch.index;
        if (tokenIndex > lastIndex) {
            segments.push({
                type: 'text',
                value: text.slice(lastIndex, tokenIndex),
            });
        }
        segments.push({
            type: 'mention',
            rawToken,
            tokenType,
            id: tokenId,
        });
        lastIndex = tokenIndex + rawToken.length;
    }
    if (lastIndex < text.length) {
        segments.push({
            type: 'text',
            value: text.slice(lastIndex),
        });
    }
    return segments;
}

/**
 * Parse raw mention token syntax without resolving it against the lookup.
 */
function parseMentionToken(rawToken: string): ResolvedMentionToken | null {
    const match = rawToken.match(MENTION_TOKEN_CAPTURE_REGEX);
    if (!match) {
        return null;
    }
    const tokenType = match[1] as MentionTokenType;
    const id = Number(match[2]);
    return {
        rawToken,
        tokenType,
        id,
        tokenKey: getMentionTokenKey(tokenType, id),
        displayName: '',
    };
}

/**
 * Resolve a mention token string using the mention lookup map.
 * @returns The resolved mention token, or `null` if not a valid mention token
 *  or not found in the mention lookup map.
 */
export function resolveMentionToken({
    rawToken,
    mentionLookup,
}: {
    rawToken: string;
    mentionLookup: MentionLookup;
}): ResolvedMentionToken | null {
    const parsed = parseMentionToken(rawToken);
    if (!parsed) {
        return null;
    }
    const mentionEntry = mentionLookup[parsed.tokenKey];
    if (!mentionEntry) {
        return null;
    }
    return {
        ...mentionEntry,
        rawToken,
        tokenKey: parsed.tokenKey,
    };
}

/**
 * Check if a mention is for the provided user or professional.
 * @param mention The mention to check.
 * @param userID The user ID of the user viewing the message.
 * @param professionalID The professional ID if in a company inbox context.
 * @returns The mention is for the provided user or professional.
 */
export function isSelfMention({
    mention,
    userID,
    professionalID,
}: {
    mention: MentionLookupEntry;
    userID?: number | null;
    professionalID?: number | null;
}): boolean {
    if (mention.tokenType === 'U') {
        return mention.id === userID;
    }
    return mention.id === professionalID;
}

/**
 * Replace known mention tokens in plain text with `@Display Name`. If
 * `renderUnknownMentions` is true, unknown tokens are replaced with `@Unknown`,
 * otherwise they are preserved as-is.
 * @param text The text to replace mention tokens in.
 * @param mentionLookup The mention lookup to use.
 * @param renderUnknownMentions Render unknown mentions as `@Unknown`.
 * @returns The text with mention tokens replaced.
 */
export function replaceMentionTokensWithDisplayText({
    text,
    mentionLookup,
    renderUnknownMentions = false,
}: {
    text: string;
    mentionLookup: MentionLookup;
    renderUnknownMentions?: boolean;
}) {
    if (!text) {
        return text;
    }
    return text.replace(MENTION_TOKEN_REGEX, (rawToken) => {
        const mention = resolveMentionToken({
            rawToken,
            mentionLookup,
        });
        if (mention) {
            return `@${mention.displayName}`;
        }
        return renderUnknownMentions ? '@Unknown' : rawToken;
    });
}

/**
 * Get the classes for an @ mention chip.
 * @param isSelfMention The mention is for the current user.
 * @returns The classes for the mention chip.
 */
export function getMentionChipClassName(isSelfMention: boolean) {
    return cn(
        'inline rounded-[4px] px-[2px] font-normal',
        isSelfMention
            ? 'bg-[rgb(255,198,0,0.2)] text-[rgb(18,100,163)]'
            : 'bg-[rgb(54,197,240,0.13)] text-[rgb(18,100,163)]',
    );
}

/**
 * Get the classes for an unknown @ mention chip.
 * @returns The classes for the unknown mention chip.
 */
export function getUnknownMentionChipClassName() {
    return 'inline rounded-[4px] bg-[hsl(0_0%_50%/0.2)] px-[2px] font-normal text-[hsl(0_0%_27%)]';
}
